import type { BookDetail } from "@contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { Button } from "../../../components/control/Button";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { useBookDetail } from "../bookContext";
import { nextBookAction } from "../workspace";

export function GuidedWorkspace() {
  const book = useBookDetail();
  const { t } = useTranslation("overview");
  const cache = useQueryClient();
  const policy = useMutation({
    mutationFn: () =>
      api<BookDetail>(`/books/${book.id}/review-policy`, {
        method: "PUT",
        body: { policy: "exceptions" },
      }),
    onSuccess: (data) => {
      cache.setQueryData(queryKeys.book(book.id), data);
    },
  });
  const action = book.nextAction ?? nextBookAction(book);
  const active = book.activeJobs.filter((j) => j.status === "running" || j.status === "queued");
  const approved = book.chapters.filter((c) => !c.excluded && c.approvedAt).length;
  const issues =
    book.qualityReviews?.filter((q) => ["attention", "failed", "stale"].includes(q.status)) ?? [];
  return (
    <section className="border-y border-line py-6" aria-label={t("workspace.title")}>
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="min-w-0 max-w-2xl">
          <h2 className="font-serif text-title font-semibold">
            {t(`workspace.action.${action.key}`)}
          </h2>
          <p className="mt-2 text-ink-2">{t(`workspace.help.${action.key}`)}</p>
        </div>
        {action.path ? (
          <Link
            className="inline-flex h-control items-center whitespace-nowrap rounded-sm bg-ink px-4 font-medium text-surface"
            to={action.path}
          >
            {t(`workspace.button.${action.key}`)}
          </Link>
        ) : null}
      </div>
      <p className="mt-5 text-data text-ink-2" role="status">
        {t("workspace.progress", {
          ready: approved,
          total: book.counts.included,
          working: active.length,
          issues: issues.length,
        })}
      </p>
      {book.audiobook ? (
        <Link className="mt-2 inline-block underline" to={`/books/${book.id}/audiobook`}>
          {t("workspace.downloadExisting")}
        </Link>
      ) : null}
      {book.reviewPolicy !== "exceptions" ? (
        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <p className="max-w-xl text-ink-2">{t("workspace.enableHelp")}</p>
          <Button variant="secondary" busy={policy.isPending} onClick={() => policy.mutate()}>
            {t("workspace.enable")}
          </Button>
          <ErrorBanner error={policy.error} />
        </div>
      ) : (
        <p className="mt-2 text-data text-ink-2">{t("workspace.automaticHelp")}</p>
      )}
      {issues.length > 0 ? (
        <ul className="mt-5 divide-y divide-line border-t border-line">
          {issues.map((q) => (
            <li key={q.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="font-medium">
                  {book.chapters.find((c) => c.id === q.chapterId)?.title}
                </p>
                <p className="text-data text-ink-2">
                  {t(`workspace.quality.${q.status}`, {
                    count: q.findings.filter((f) => !f.accepted).length,
                  })}
                </p>
              </div>
              <Link
                className="whitespace-nowrap underline"
                to={`/books/${book.id}/review/${q.chapterId}`}
              >
                {t("workspace.openFinding")}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
